import { createEnv } from "@t3-oss/env-core";
import * as val from "valibot";

const readEnv = (key: string) => {
  if (typeof window === "undefined") {
    // `||` rather than `??` is intentional: Cloudflare's nodejs_compat shim
    // returns "" (not undefined) for unset keys, so `??` would preserve the
    // empty string and createEnv's `emptyStringAsUndefined` would then reject
    // the value. With `||`, empty strings fall through to the import.meta.env
    // value baked in at build time, making the SSR worker self-sufficient
    // without per-env wrangler secret pushes.
    return process.env[key] || import.meta.env[key];
  }
  return import.meta.env[key];
};

const envConfig = {
  server: {
    SENTRY_AUTH_TOKEN: val.optional(val.string()),
  },
  client: {
    VITE_SPACETIMEDB_HOST: val.string(),
    VITE_SPACETIMEDB_DB_NAME: val.string(),
    VITE_TYCHO_ENV: val.union([val.literal("dev"), val.literal("staging"), val.literal("prod")]),
    VITE_CLERK_PUBLISHABLE_KEY: val.string(),
    VITE_CLERK_SPACETIMEDB_JWT_TEMPLATE: val.string(),
    VITE_RUTTER_PUBLIC_KEY: val.string(),
    VITE_WITHCO_IMAGE_CDN_BASE: val.pipe(val.string(), val.url()),
    VITE_SENTRY_DSN: val.optional(val.string()),
    VITE_AXIOM_TOKEN: val.string("set me"),
    VITE_AXIOM_DATASET: val.string(),
    VITE_GAPI_PLACES_KEY: val.string(),
    VITE_POSTHOG_TOKEN: val.string(),
    VITE_MAPBOX_TOKEN: val.string(),
    VITE_COMMUNITY_THEATER_CARD_ID: val.string(),
    // Optional override for the "Schedule a call" Calendly event type. Prod/staging
    // leave it unset (the ScheduleCallAction default applies); the opt-in
    // real-integrations e2e spec points it at a throwaway test event type so a run
    // doesn't book onto a real calendar.
    VITE_CALENDLY_URL: val.optional(val.pipe(val.string(), val.url())),
    // Canonical origin of this app (no trailing slash) — og:url and other
    // absolute self-links. Unset means production's current origin; see
    // `lib/siteUrls.ts`. Set per GitHub Environment during the with.co move.
    VITE_APP_ORIGIN: val.optional(val.pipe(val.string(), val.url())),
    // The static marketing (manifesto) site this app links out to.
    VITE_MARKETING_SITE_URL: val.optional(val.pipe(val.string(), val.url())),
  },
  clientPrefix: "VITE",
  runtimeEnvStrict: {
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    VITE_SPACETIMEDB_HOST: readEnv("VITE_SPACETIMEDB_HOST"),
    VITE_SPACETIMEDB_DB_NAME: readEnv("VITE_SPACETIMEDB_DB_NAME"),
    VITE_TYCHO_ENV: readEnv("VITE_TYCHO_ENV"),
    VITE_CLERK_PUBLISHABLE_KEY: readEnv("VITE_CLERK_PUBLISHABLE_KEY"),
    VITE_CLERK_SPACETIMEDB_JWT_TEMPLATE: readEnv("VITE_CLERK_SPACETIMEDB_JWT_TEMPLATE"),
    VITE_RUTTER_PUBLIC_KEY: readEnv("VITE_RUTTER_PUBLIC_KEY"),
    VITE_WITHCO_IMAGE_CDN_BASE: readEnv("VITE_WITHCO_IMAGE_CDN_BASE"),
    VITE_SENTRY_DSN: readEnv("VITE_SENTRY_DSN"),
    VITE_AXIOM_TOKEN: readEnv("VITE_AXIOM_TOKEN"),
    VITE_AXIOM_DATASET: readEnv("VITE_AXIOM_DATASET"),
    VITE_GAPI_PLACES_KEY: readEnv("VITE_GAPI_PLACES_KEY"),
    VITE_POSTHOG_TOKEN: readEnv("VITE_POSTHOG_TOKEN"),
    VITE_MAPBOX_TOKEN: readEnv("VITE_MAPBOX_TOKEN"),
    VITE_COMMUNITY_THEATER_CARD_ID: readEnv("VITE_COMMUNITY_THEATER_CARD_ID"),
    VITE_CALENDLY_URL: readEnv("VITE_CALENDLY_URL"),
    VITE_APP_ORIGIN: readEnv("VITE_APP_ORIGIN"),
    VITE_MARKETING_SITE_URL: readEnv("VITE_MARKETING_SITE_URL"),
  },
  emptyStringAsUndefined: true,
} as const;

export const env = createEnv(envConfig);
